import React from 'react';
import TrophySvg from './TrophySvg';
import { canUserEnterComp, compMembershipRestrictionText, membershipTypeAllowedForComp } from '../utils/membership';
import { NON_MEMBER_GUEST_MEMBERSHIP_TYPE } from '../constants/membership';
import { compTakesTeamEntries, compBuildsTeamsFromDraw } from '../utils/competition';
import MyClubTeamPicker, { clubTeamEntry, useMyClubTeams } from './MyClubTeamPicker';

/* ====================================================================
   COMPETITION REGISTRATION FLOW — player self-registers from QR/link
   ==================================================================== */
function CompRegistrationFlow({ API, compId, currentUser, onDone, onRegistered }) {
  const [comp, setComp] = React.useState(null);
  const [myReg, setMyReg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [teamName, setTeamName] = React.useState('');
  const [teamMembers, setTeamMembers] = React.useState([{ id: '', name: '', email: '' }]);
  const [clubPlayers, setClubPlayers] = React.useState([]);
  const [playerSearch, setPlayerSearch] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  // The club team being entered: an id, 'new' for a team typed in by hand,
  // or null while the player has yet to choose.
  const [pickedTeam, setPickedTeam] = React.useState(null);
  const myClubTeams = useMyClubTeams(API, comp, currentUser.id);

  React.useEffect(() => {
    (async () => {
      try {
        const [compRes, regRes] = await Promise.all([
          fetch(`${API}/api/competitions/${compId}`),
          fetch(`${API}/api/registrations/${compId}/my`),
        ]);
        if (compRes.ok) setComp(await compRes.json());
        if (regRes.ok) setMyReg(await regRes.json());
        const pRes = await fetch(`${API}/api/users`);
        if (pRes.ok) setClubPlayers(await pRes.json());
      } catch {}
      setLoading(false);
    })();
  }, [API, compId]); // eslint-disable-line

  const pickClubTeam = (ct) => {
    const entry = clubTeamEntry(ct, comp, currentUser.id, clubPlayers);
    setPickedTeam(ct.id);
    setError('');
    setTeamName(entry.teamName);
    setTeamMembers(entry.teamMembers);
  };

  const enterNewTeam = () => {
    setPickedTeam('new');
    setError('');
    setTeamName('');
    setTeamMembers([{ id: '', name: '', email: '' }]);
  };

  const addMember = () => setTeamMembers(prev => [...prev, { id: '', name: '', email: '' }]);
  const removeMember = (i) => setTeamMembers(prev => prev.filter((_, j) => j !== i));
  const updateMember = (i, field, val) => {
    setTeamMembers(prev => prev.map((m, j) => j === i ? { ...m, [field]: val } : m));
  };
  const selectPlayer = (i, userId) => {
    if (!userId) {
      setTeamMembers(prev => prev.map((m, j) => j === i ? { id: '', name: '', email: '' } : m));
      return;
    }
    const pl = clubPlayers.find(u => String(u.id) === String(userId));
    if (pl) {
      setTeamMembers(prev => prev.map((m, j) => j === i ? { id: pl.id, name: pl.name, email: pl.email || '' } : m));
    }
  };

  const handleRegister = async () => {
    if (compTakesTeamEntries(comp)) {
      /* The team name was not checked here, only the team-mates — so leaving
         it blank let the whole entry through as though it were a solo one. */
      if (!teamName.trim()) {
        setError('Give your team a name before submitting registration.');
        return;
      }
      const requiredExtraPlayers = Math.max(0, (comp.playersPerTeam || 1) - 1);
      const providedExtraPlayers = teamMembers.filter(m => String(m.name || '').trim()).length;
      if (providedExtraPlayers < requiredExtraPlayers) {
        setError(`Add ${requiredExtraPlayers} team member${requiredExtraPlayers === 1 ? '' : 's'} before submitting registration.`);
        return;
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const body = {};
      /* Guarded on the format alone, not on the team name being filled in.
         It used to be `&& teamName.trim()`, which meant a blank name threw
         away the team-mates the player had just typed in as well — so the
         request went out empty and the entry became a solo one with no
         indication that anything had been dropped. The name is required
         above, so by here there is one. */
      if (compTakesTeamEntries(comp)) {
        body.teamName = teamName.trim();
        if (pickedTeam && pickedTeam !== 'new') body.clubTeamId = pickedTeam;
        body.teamMembers = teamMembers
          .filter(m => m.name.trim())
          .map(m => ({ id: m.id || undefined, name: m.name, email: m.email }));
      }
      const r = await fetch(`${API}/api/registrations/${compId}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Registration failed');
      setMyReg(data);
      if (onRegistered) onRegistered();
    } catch (err) {
      setError(err.message || 'Registration failed');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="w3-center w3-padding-64"><i className="fa fa-spinner fa-spin fa-3x"></i></div>;
  if (!comp) return <div className="w3-center w3-padding-64"><p>Competition not found</p><button className="w3-button w3-round w3-theme" onClick={onDone}>Go to Dashboard</button></div>;

  const isTeamFormat = compTakesTeamEntries(comp);
  const teamsDrawn = compBuildsTeamsFromDraw(comp);
  // Team-mates have to clear the same membership rule as the registrant, so
  // offer only players who do rather than failing on submit.
  const availablePlayers = clubPlayers.filter(u => u.id !== currentUser.id && canUserEnterComp(comp, u));
  const guestsAllowed = membershipTypeAllowedForComp(comp, NON_MEMBER_GUEST_MEMBERSHIP_TYPE);
  const normalizedPlayerSearch = String(playerSearch || '').trim().toLowerCase();
  const requiredExtraPlayers = isTeamFormat ? Math.max(0, (comp.playersPerTeam || 1) - 1) : 0;
  const providedExtraPlayers = teamMembers.filter(m => String(m.name || '').trim()).length;
  const hasEnoughTeamMembers = !isTeamFormat || providedExtraPlayers >= requiredExtraPlayers;
  /* The button used to be gated on the team-mates alone, so an entry with
     every team-mate filled in and the name left blank was submittable — and
     went through as a solo entry that appeared in no team and no draw. */
  const hasTeamName = !isTeamFormat || !!teamName.trim();
  const readyToSubmit = hasTeamName && hasEnoughTeamMembers;
  const canRetry = myReg && ['rejected', 'withdrawn'].includes(myReg.status);
  const choosingTeam = myClubTeams.length > 0 && pickedTeam === null;
  const pickedClubTeam = myClubTeams.find(ct => ct.id === pickedTeam);
  // The comp may be limited to certain membership types. The server refuses
  // either way; this is so the form is not offered only to be rejected.
  const eligible = canUserEnterComp(comp, currentUser);
  const restrictionText = compMembershipRestrictionText(comp);

  return (
    <div className="w3-modal" style={{ display: 'block' }}>
      <div className="w3-modal-content w3-card-4 w3-round" style={{ maxWidth: '700px' }}>
        <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '1.1em' }}>
            <TrophySvg size={16} style={{ marginRight: '8px' }} />{comp.name}
          </span>
          <button className="w3-button w3-round" onClick={onDone}><i className="fa fa-times"></i></button>
        </div>
        <div className="w3-container w3-padding-16">
          {myReg && !canRetry && (
            <div className="w3-center w3-padding">
              <i className={`fa fa-3x ${myReg.status === 'approved' ? 'fa-check-circle w3-text-green' : myReg.status === 'pending' ? 'fa-clock-o w3-text-orange' : 'fa-times-circle w3-text-red'}`}></i>
              <h4>
                {myReg.status === 'approved' && 'You are registered!'}
                {myReg.status === 'pending' && 'Registration Pending'}
                {myReg.status === 'rejected' && 'Registration Declined'}
                {myReg.status === 'withdrawn' && 'Registration Withdrawn'}
              </h4>
              <p className="w3-text-grey">
                {myReg.status === 'pending' && 'An admin will review your registration shortly.'}
                {myReg.status === 'approved' && 'You have been added to this competition.'}
              </p>
              <button className="w3-button w3-theme w3-round w3-margin-top" onClick={onDone}>
                <i className="fa fa-home w3-margin-right"></i>Go to Dashboard
              </button>
            </div>
          )}

          {!myReg && comp.registrationClosed && (
            <div className="w3-center w3-padding">
              <i className="fa fa-lock fa-3x w3-text-red"></i>
              <h4>Registration Closed</h4>
              <p className="w3-text-grey">Registration for this competition is no longer open.</p>
              <button className="w3-button w3-theme w3-round w3-margin-top" onClick={onDone}>
                <i className="fa fa-home w3-margin-right"></i>Go to Dashboard
              </button>
            </div>
          )}

          {(!myReg || canRetry) && !comp.registrationClosed && !eligible && (
            <div className="w3-center w3-padding">
              <i className="fa fa-id-card fa-3x w3-text-orange"></i>
              <h4>Not open to your membership</h4>
              <p className="w3-text-grey">
                This competition is only open to: {restrictionText}.<br />
                You are registered as {String(currentUser.membershipType || '').trim() || 'no membership type'}.<br />
                Update your membership with the club to enter this competition.
              </p>
              <button className="w3-button w3-theme w3-round w3-margin-top" onClick={onDone}>
                <i className="fa fa-home w3-margin-right"></i>Go to Dashboard
              </button>
            </div>
          )}

          {canRetry && !comp.registrationClosed && eligible && (
            <div className="w3-panel w3-pale-red w3-round w3-padding-small">
              <i className="fa fa-info-circle w3-margin-right"></i>Your previous registration was {myReg.status === 'rejected' ? 'declined' : 'withdrawn'}. You can submit a new registration below.
            </div>
          )}

          {(!myReg || canRetry) && !comp.registrationClosed && eligible && (
            <>
              <div className="w3-panel w3-theme-l5 w3-round w3-padding">
                <p><i className="fa fa-info-circle w3-margin-right"></i><strong>Format:</strong> {comp.format} — {comp.playersPerTeam} player(s) per team</p>
                {/* Said here rather than left to be inferred from the absence
                    of a team form: a player who came expecting to name their
                    partners should be told who does it instead. */}
                {teamsDrawn && (
                  <p><i className="fa fa-random w3-margin-right"></i>
                    <strong>Teams:</strong> drawn by the club — enter on your own and you will be put in a team.
                  </p>
                )}
                {comp.startDate && <p><i className="fa fa-calendar w3-margin-right"></i><strong>Date:</strong> {comp.startDate}</p>}
              </div>

              {error && <div className="w3-panel w3-red w3-round w3-padding-small"><small>{error}</small></div>}

              <p className="w3-text-grey">Registering as <strong>{currentUser.name}</strong> ({currentUser.email})</p>

              {choosingTeam && <MyClubTeamPicker teams={myClubTeams} onPick={pickClubTeam} onNew={enterNewTeam} />}

              {isTeamFormat && !choosingTeam && (
                <div className="w3-margin-bottom">
                  {myClubTeams.length > 0 && (
                    <button className="w3-button w3-small w3-round w3-theme-l4 w3-margin-bottom" onClick={() => setPickedTeam(null)}>
                      <i className="fa fa-arrow-left w3-margin-right"></i>Choose a different team
                    </button>
                  )}
                  <label className="w3-small w3-text-grey">Team Name</label>
                  {/* A club team keeps its own name; renaming it is done on the team, not on an entry. */}
                  <input className="w3-input w3-border w3-round w3-margin-bottom" placeholder="e.g. The Kiwis"
                    value={teamName} readOnly={!!pickedClubTeam}
                    style={pickedClubTeam ? { background: 'var(--gs-bg)' } : undefined}
                    onChange={e => setTeamName(e.target.value)} />

                  <label className="w3-small w3-text-grey">Team Members (you + {comp.playersPerTeam - 1} more needed)</label>
                  {!guestsAllowed && restrictionText && (
                    <p className="w3-small w3-text-grey" style={{ margin: '4px 0' }}>
                      Only club players whose membership is {restrictionText} can be added.
                    </p>
                  )}
                  <input
                    className="w3-input w3-border w3-round w3-margin-top w3-margin-bottom"
                    placeholder="Search by member name or member ID"
                    value={playerSearch}
                    onChange={e => setPlayerSearch(e.target.value)}
                  />
                  {teamMembers.map((m, i) => (
                    <div key={i} style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select className="w3-select w3-border w3-round" style={{ flex: 2 }}
                          value={m.id || ''}
                          onChange={e => {
                            if (e.target.value === '__new__') selectPlayer(i, '');
                            else selectPlayer(i, e.target.value);
                          }}>
                          <option value="">-- Select club player or type below --</option>
                          {/* The chosen player is always listed, so a team-mate filled in from a
                              club team still shows when the search or the club list leaves them out. */}
                          {m.id && <option value={m.id}>{m.name}</option>}
                          {availablePlayers
                            .filter(p => p.id !== m.id)
                            .filter(p => !teamMembers.some((tm, ti) => ti !== i && tm.id && tm.id === p.id))
                            .filter((p) => {
                              if (!normalizedPlayerSearch) return true;
                              const nameText = String(p.name || '').toLowerCase();
                              const memberIdText = String(p.membershipNumber || '').toLowerCase();
                              return nameText.includes(normalizedPlayerSearch) || memberIdText.includes(normalizedPlayerSearch);
                            })
                            .map(p => <option key={p.id} value={p.id}>{p.name} (ID: {p.membershipNumber || 'Not assigned'})</option>)}
                          {guestsAllowed && <option value="__new__">+ Add new player</option>}
                        </select>
                        {teamMembers.length > 1 && (
                          <button className="w3-button w3-round w3-tiny w3-red" onClick={() => removeMember(i)}>
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                      {!m.id && guestsAllowed && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <input className="w3-input w3-border w3-round" style={{ flex: 2 }} placeholder="Player name"
                            value={m.name} onChange={e => updateMember(i, 'name', e.target.value)} />
                          <input className="w3-input w3-border w3-round" style={{ flex: 2 }} placeholder="Email (optional)"
                            value={m.email} onChange={e => updateMember(i, 'email', e.target.value)} />
                        </div>
                      )}
                    </div>
                  ))}
                  <button className="w3-button w3-small w3-round w3-theme-l4" onClick={addMember}>
                    <i className="fa fa-plus w3-margin-right"></i>Add Team Member
                  </button>
                </div>
              )}

              {!choosingTeam && <>
              <button className="w3-button w3-theme w3-round w3-block w3-margin-top" onClick={handleRegister} disabled={submitting || !readyToSubmit}>
                {submitting ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-check w3-margin-right"></i>Register for Competition</>}
              </button>
              {isTeamFormat && (
                <p className="w3-small w3-text-grey" style={{ marginTop: '6px', marginBottom: 0 }}>
                  A confirmation email goes to every player on the team who has an email address.
                </p>
              )}
              {!hasEnoughTeamMembers && (
                <p className="w3-small w3-text-red" style={{ marginTop: '6px', marginBottom: 0 }}>
                  Add {requiredExtraPlayers} team member{requiredExtraPlayers === 1 ? '' : 's'} (currently {providedExtraPlayers}) to enable submission.
                </p>
              )}
              {hasEnoughTeamMembers && !hasTeamName && (
                <p className="w3-small w3-text-red" style={{ marginTop: '6px', marginBottom: 0 }}>
                  Give your team a name to enable submission.
                </p>
              )}
              </>}
              <p className="w3-center w3-small w3-text-grey w3-margin-top">
                Your registration will be reviewed by an admin before being confirmed.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompRegistrationFlow;
